import React, {useEffect} from 'react';
import {FormattedMessage} from 'react-intl';
import classnames from 'classnames';
import PageHeader from '../../../../common/components/ui/PageHeader';
import MenuItem, {MenuItemProps} from '../../components/MenuItem';
import {DashboardChart} from '../../components/DashboardChart';
import ButtonUnreadMessages from '../../components/ButtonUnreadMessages';
import FooterLogo from '../../../../common/components/ui/FooterLogo';
import {useGlobalContext} from '../../../../common/components/core/GlobalContext';
import {useUserSessionContext} from '../../../../common/components/core/UserSessionContext';
import {useRegFormsContext} from '../../../RegForms/components/RegFormsContext';
import {DashboardContextProvider} from '../../components/DashboardContext';
import {ListUnreadMessagesContextProvider} from '../../components/ListUnreadMessagesContext';
import Grid from '../../components/Grid';
import {Format} from '../../../../common/utils';
import {pathnames as P} from '../../../../common/enums/pathnames';
import doc from '../../../../common/assets/icons/document-text-outline.svg';
import card from '../../../../common/assets/icons/card-outline.svg';
import invoices from '../../../../common/assets/icons/list.svg';
import invoiceOrders from '../../../../common/assets/icons/list-dropdown.svg';
import receipts from '../../../../common/assets/icons/document-attachment.svg';
import transfers from '../../../../common/assets/icons/arrows-exchange.svg';
import LocaleSelector from "../../../../common/components/core/LocaleSelector";
import PopupRedirectToForm101 from '../../../../common/components/ui/PopupRedirectToForm101';
import styles from './Dashboard.module.scss';

export type DashboardItemOption = MenuItemProps & {
  key: number;
}

export const menuItems: DashboardItemOption[] = [
  {
    key: 0,
    label: (
      <div className={styles.orderInvoice}>
        <img src={doc} alt="doc" className={styles.orderInvoiceIcon}/>
        <div className={styles.orderInvoiceText}>
          <FormattedMessage
            id={'dashboard-link-orderInvoice'}
            defaultMessage={'Order Invoice'}
          />
        </div>
      </div>
    ),
    link: P.ORDER_INVOICE
  },
  {
    key: 1,
    label: (
      <span className={styles.payment}>
        <img src={card} alt="payment" className={styles.paymentIcon}/>
        <FormattedMessage
          id={'dashboard-link-payment'}
          defaultMessage={'Payment'}
        />
      </span>
    ),
    link: P.PAYMENT
  },
  {
    key: 2,
    label: (
      <span className={styles.disabled}>
        <FormattedMessage
          id={'dashboard-link-calculator'}
          defaultMessage={'Calculator'}
        />
      </span>
    ),
    link: P.CALCULATOR
  },
  {
    key: 3,
    label: (
      <FormattedMessage
        id={'dashboard-link-regForms'}
        defaultMessage={'Registration forms'}
      />
    ),
    link: P.REG_FORMS
  },
  {
    key: 4,
    label: (
      <>
        <FormattedMessage
          id={'dashboard-link-invoices'}
          defaultMessage={'Invoices'}
        />
        <img src={invoices} alt="icon" className={styles.linkIcon}/>
      </>
    ),
    link: P.INVOICES
  },
  {
    key: 5,
    label: (
      <>
        <FormattedMessage
          id={'dashboard-link-invoiceOrders'}
          defaultMessage={'Invoice Orders'}
        />
        <img src={invoiceOrders} alt="icon" className={styles.linkIcon}/>
      </>
    ),
    link: P.INVOICE_ORDERS
  },
  {
    key: 6,
    label: (
      <>
        <FormattedMessage
          id={'dashboard-link-receipts'}
          defaultMessage={'Receipts'}
        />
        <img src={receipts} alt="icon" className={styles.linkIcon}/>
      </>
    ),
    link: P.RECEIPTS
  },
  {
    key: 7,
    label: (
      <>
        <FormattedMessage
          id={'dashboard-link-transfers'}
          defaultMessage={'Transfers'}
        />
        <img src={transfers} alt="icon" className={styles.linkIcon}/>
      </>
    ),
    link: P.TRANSFERS
  },
  {
    key: 8,
    label: (
      <FormattedMessage
        id={'dashboard-link-settings'}
        defaultMessage={'Settings'}
      />
    ),
    link: P.SETTINGS
  },
  {
    key: 9,
    label: (
      <FormattedMessage
        id={'dashboard-link-inviteFriend'}
        defaultMessage={'Invite friend'}
      />
    ),
    link: P.INVITE_FRIEND
  },
  {
    key: 10,
    label: (
      <FormattedMessage
        id={'dashboard-link-logout'}
        defaultMessage={'Logout'}
      />
    ),
    link: P.LOGOUT,
  }
];

const Dashboard = () => {
  const {handleLogout, currentUser} = useUserSessionContext();
  const {form101FileStatus, status} = useRegFormsContext();
  const isForm101Filled = form101FileStatus.exists;
  const {setPageTitle} = useGlobalContext();
  const username = Format.formatUserName(currentUser?.profile?.firstName, currentUser?.profile?.lastName);
  const {paymentPageNotAllowed, invoiceOrderPageNotAllowed} = useUserSessionContext();

  useEffect(() => {
    setPageTitle((
      <PageHeader
        id={'dashboard-title'}
        defaultMessage={'Self employed'}
        showLogo={true}
        secondaryHeader={{
          id: 'dashboard-secondary-title',
          defaultMessage: 'Welcome',
          className: styles.pageSecondaryHeader
        }}
      />
    ));
  }, []);

  const getFirstColumn = (firstColumnKeys: number[]) => {

    return menuItems
      .filter(item => firstColumnKeys.includes(item.key))
      .map((item) => {
        if (item.key === 0 && !isForm101Filled) {
          return <PopupRedirectToForm101 {...item} type={'button'} redirectLink={status.form101.link}/>
        } else if (item.key === 0 && invoiceOrderPageNotAllowed) {
          return <div/>
        } else if (item.key === 1 && paymentPageNotAllowed) {
          return <div/>
        } else {
          return <MenuItem {...item} className={styles.linkContainer}/>;
        }
      });
  };

  const getSecondColumn = () => {
    const secondColumnKeys = [4, 5, 6, 7];

    return menuItems
      .filter(item => secondColumnKeys.includes(item.key))
      .map((item) => {
        return <MenuItem {...item} className={styles.linkItem}/>;
      });
  };

  const getButtons = () => {
    const buttonsKeys = [8, 9, 10];

    return menuItems
      .filter(item => buttonsKeys.includes(item.key))
      .map((item) => {
        if (item.key === 9) {
          return <MenuItem {...item} className={styles.buttonInvite}/>;
        } else if (item.key === 10) {
          return <MenuItem {...item} onClick={handleLogout} className={styles.buttonLogout}/>;
        } else {
          return <MenuItem {...item} className={styles.buttonSettings}/>;
        }
      });
  };

  const getMobileButtons = () => {
    const buttonsKeys = [8, 3, 9, 10];
    const array = menuItems.filter(item => buttonsKeys.includes(item.key));

    return [array[1], array[0], array[2], array[3]]
      .filter(Boolean)
      .map((item) => {
        if (item.key === 9) {
          return <MenuItem {...item} className={styles.buttonInvite}/>;
        } else if (item.key === 10) {
          return <MenuItem {...item} onClick={handleLogout} className={styles.buttonLogout}/>;
        } else {
          return <MenuItem {...item} className={styles.buttonSettings}/>;
        }
      });
  };

  return (
    <DashboardContextProvider>
      <ListUnreadMessagesContextProvider>
        <div>
          <div className={styles.dashboardHeaderContainer}>
            <div className={styles.header}>
              <FormattedMessage
                id={'dashboard-header'}
                defaultMessage={'Hello, {username}'}
                values={{username}}
              />
            </div>
            <div className={styles.localeSelectorContainer}>
              <LocaleSelector/>
            </div>
          </div>
          <div className={styles.desktop}>
            <div className={styles.desktopDashboardContainer}>
              {getFirstColumn([0, 1, 3])}
              <div className={styles.linkContainer}>
                <DashboardChart/>
              </div>
              <div className={styles.linkItemsContainer}>
                {getSecondColumn()}
              </div>
              <Grid/>
            </div>
            <div className={styles.desktopButtonsContainer}>
              {getButtons()}
            </div>
            <ButtonUnreadMessages className={styles.buttonUnreadMessagesDesktop}/>
          </div>
          <div className={styles.mobile}>
            <ButtonUnreadMessages className={styles.buttonUnreadMessagesMobile}/>
            <div className={classnames(styles.linkContainer, styles.chartContainer)}>
              <DashboardChart/>
            </div>
            <div className={classnames(
              styles.mobileFirstColumnContainer,
              {[styles.mobileIsPaymentNotAllowed]: paymentPageNotAllowed && invoiceOrderPageNotAllowed},
            )}>
              {getFirstColumn([0, 1])}
            </div>
            <div className={styles.linkItemsContainer}>
              {getSecondColumn()}
            </div>
            {getMobileButtons()}
          </div>
          <FooterLogo/>
        </div>
      </ListUnreadMessagesContextProvider>
    </DashboardContextProvider>
  );
};

export default Dashboard;